/**
 * Conference career rankings from the class summary (2010+).
 * Current conference membership is applied to every season.
 */

import type { ClassSummaryFile } from '../types/classSummary';
import { PLAYER, ROW } from '../types/classSummary';
import {
  ClassStatCategory,
  classRowPoints,
  classRowStatPoints,
  parseClassStatParam,
} from './classCompare';
import {
  CAREER_CATEGORY_COLUMNS,
  CAREER_SEGMENT,
  CareerFilters,
  CareerYearRange,
  matchesCareerFilters,
} from './collegeCareer';
import {
  ConferenceMeta,
  OTHER_CONFERENCE_ID,
  resolveCollegeMeta,
  resolveConferenceMeta,
} from './collegeMeta';
import { getConferenceIdFromAbbreviation, isValidConferenceAbbreviation } from './conferenceUtils';

export interface ConferenceCareerPath {
  abbr: string | null;
  stat: ClassStatCategory;
}

export interface ConferenceCareerEntityRow {
  id: string;
  slug: string | null;
  name: string;
  logo: string | null;
  playerCount: number;
  collegeCount: number;
  categoryPoints: number;
  totalPoints: number;
  categories: number[];
  usedYtd: boolean;
}

export interface ConferenceCareerTable {
  view: 'conferences' | 'colleges';
  conference: ConferenceMeta | null;
  stat: ClassStatCategory;
  fromYear: number;
  toYear: number;
  rows: ConferenceCareerEntityRow[];
  totalPoints: number;
  playerCount: number;
  collegeCount: number;
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
}

const isClassStatSegment = (value: string): boolean => {
  const stat = parseClassStatParam(value);
  return Boolean(stat) && stat === value.trim().toLowerCase();
};

export const isConferenceCareerSlug = (value: string): boolean => {
  const slug = value.trim().toLowerCase();
  return slug === OTHER_CONFERENCE_ID || isValidConferenceAbbreviation(slug);
};

export const resolveCareerConferenceId = (slug: string): string | null => {
  const wanted = slug.trim().toLowerCase();
  if (wanted === OTHER_CONFERENCE_ID) return OTHER_CONFERENCE_ID;
  return getConferenceIdFromAbbreviation(wanted);
};

export const parseConferenceCareerPath = (pathname: string): ConferenceCareerPath | null => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'conferences' || parts[1] !== CAREER_SEGMENT) return null;
  if (parts.length > 4) return null;
  if (!parts[2]) return { abbr: null, stat: '' };
  if (isClassStatSegment(parts[2]) && !parts[3]) {
    return { abbr: null, stat: parseClassStatParam(parts[2]) };
  }
  if (!isConferenceCareerSlug(parts[2])) return null;
  return {
    abbr: parts[2].toLowerCase(),
    stat: parseClassStatParam(parts[3]),
  };
};

export const getConferenceCareerPath = (
  abbr: string | null = null,
  stat: string = ''
): string => {
  const category = parseClassStatParam(stat);
  const conference = abbr?.trim().toLowerCase() || '';
  if (conference && category) return `/conferences/${CAREER_SEGMENT}/${conference}/${category}`;
  if (conference) return `/conferences/${CAREER_SEGMENT}/${conference}`;
  if (category) return `/conferences/${CAREER_SEGMENT}/${category}`;
  return `/conferences/${CAREER_SEGMENT}`;
};

const emptyCategories = (): number[] => CAREER_CATEGORY_COLUMNS.map(() => 0);

export const buildConferenceCareerTable = (
  summary: ClassSummaryFile,
  yearRange: CareerYearRange,
  filters: CareerFilters,
  conferenceId: string | null = null
): ConferenceCareerTable => {
  const byEntity = new Map<
    string,
    {
      meta: ReturnType<typeof resolveCollegeMeta> | ConferenceMeta;
      players: Set<number>;
      colleges: Set<string>;
      categoryPoints: number;
      totalPoints: number;
      categories: number[];
      usedYtd: boolean;
    }
  >();
  const allPlayers = new Set<number>();
  const allColleges = new Set<string>();

  for (const row of summary.rows) {
    const player = summary.players[row[ROW.playerIndex]];
    if (!player) continue;
    if (!matchesCareerFilters(row, player[PLAYER.classYear], player[PLAYER.round], yearRange, filters)) {
      continue;
    }

    const collegeId = String(row[ROW.collegeId]);
    const college = resolveCollegeMeta(collegeId);
    const conference = resolveConferenceMeta(college.conferenceId);
    if (conferenceId && conference.id !== conferenceId) continue;

    const entityId = conferenceId ? college.id : conference.id;
    const entityMeta = conferenceId ? college : conference;
    const usedYtd = Boolean(
      summary.currentSeasonIncomplete && row[ROW.season] === summary.toYear
    );
    const categoryPoints = classRowStatPoints(row, summary, filters.stat);
    const totalPoints = classRowPoints(row, summary);
    allPlayers.add(row[ROW.playerIndex]);
    allColleges.add(collegeId);

    let existing = byEntity.get(entityId);
    if (!existing) {
      existing = {
        meta: entityMeta,
        players: new Set(),
        colleges: new Set(),
        categoryPoints: 0,
        totalPoints: 0,
        categories: emptyCategories(),
        usedYtd: false,
      };
      byEntity.set(entityId, existing);
    }
    existing.players.add(row[ROW.playerIndex]);
    existing.colleges.add(collegeId);
    existing.categoryPoints += categoryPoints;
    existing.totalPoints += totalPoints;
    existing.usedYtd = existing.usedYtd || usedYtd;
    CAREER_CATEGORY_COLUMNS.forEach((column, index) => {
      existing.categories[index] += row[column.rowIndex] || 0;
    });
  }

  const rows: ConferenceCareerEntityRow[] = Array.from(byEntity.entries())
    .map(([id, item]) => ({
      id,
      slug: item.meta.slug,
      name: item.meta.name,
      logo: item.meta.logo,
      playerCount: item.players.size,
      collegeCount: item.colleges.size,
      categoryPoints: parseFloat(item.categoryPoints.toFixed(2)),
      totalPoints: parseFloat(item.totalPoints.toFixed(2)),
      categories: item.categories.map((value) => parseFloat(value.toFixed(2))),
      usedYtd: item.usedYtd,
    }))
    .sort(
      (a, b) =>
        b.categoryPoints - a.categoryPoints ||
        b.totalPoints - a.totalPoints ||
        a.name.localeCompare(b.name)
    );

  return {
    view: conferenceId ? 'colleges' : 'conferences',
    conference: conferenceId ? resolveConferenceMeta(conferenceId) : null,
    stat: filters.stat,
    fromYear: yearRange.from,
    toYear: yearRange.to,
    rows,
    totalPoints: parseFloat(rows.reduce((sum, row) => sum + row.categoryPoints, 0).toFixed(2)),
    playerCount: allPlayers.size,
    collegeCount: allColleges.size,
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
  };
};

export const conferenceCareerUsesCurrentSeason = (table: ConferenceCareerTable): boolean =>
  table.currentSeasonIncomplete &&
  table.fromYear <= table.currentSeason &&
  table.toYear >= table.currentSeason;
